import React, { createRef, useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

import { isClient, isWeb, View } from '@tamagui/core';
import { DialogContent, DialogOverlay, Dialog as TamaguiDialog } from '@tamagui/dialog';
import type { DialogContentProps, DialogOverlayProps } from '@tamagui/dialog';
import {
  AccessibilityInfo,
  Animated,
  Easing,
  PanResponder,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import type {
  GestureResponderEvent,
  PanResponderInstance,
  Image as ReactNativeImage,
  View as RNView,
} from 'react-native';

import { Box } from '../../../../components/Box';
import { PressableBox } from '../../../../components/Box/PressableBox';
import {
  ChevronBackButton,
  ChevronForwardButton,
  CloseButton,
} from '../../../../components/Button/IconButton';
import { Image } from '../../../../components/Image';
import { Text } from '../../../../components/Text';
import { tamaguiElement } from '../../../../helpers/tamaguiElement';
import { BREAKPOINTS } from '../../../../media';
import {
  CABINET_QUERY_PARAMS_FOR_LIGHTBOX_PRIMARY,
  CABINET_QUERY_PARAMS_LIGHTBOX_CAROUSEL,
} from './helpers';

const LightboxDialogContent = tamaguiElement<DialogContentProps>(DialogContent);
const LightboxDialogOverlay = tamaguiElement<DialogOverlayProps>(DialogOverlay);

/**
 * Lightbox component that displays a carousel of images
 */
export interface LightboxProps {
  /**
   * The name of the analytics event
   */
  analyticsName: string;
  /**
   * The footer
   */
  footer: React.ReactNode;
  /**
   * Whether the lightbox is open
   */
  isOpen: boolean;
  /**
   * Function to close the lightbox
   */
  handleClose: () => void;
  /**
   * A title component to display on mobile viewports
   */
  mobileTitle: React.ReactNode;
  /**
   * Array of photo urls
   */
  photoUrls: string[];
  /**
   * The index of the selected photo
   */
  selectedPhotoIndex?: number | null;
}

/**
 * Represents the last navigation source.
 */
type LastNavigationSource = {
  /**
   * The source of the navigation.
   * Button here represents the chevron buttons.
   * Keyboard represents the keyboard arrow keys.
   */
  source: 'keyboard' | 'button';
  /**
   * Indicates whether the keyboard was activated during the navigation.
   */
  keyboardActivated: boolean;
};

/**
 * Lightbox component for use on web and native and make sure all of your photo URLs come from cabinet.
 * @param {string} analyticsName - The name of the analytics event
 * @param {React.ReactNode} footer - The footer component to render in the Lightbox
 * @param {string[]} photoUrls - A list of photoUrls to render in the Lightbox
 * @param {boolean} isOpen - indicates the state of the lightbox
 * @param {Function} handleClose - function to control when the Lightbox should be closed
 * @param {React.ReactNode} mobileTitle - a title component to display on mobile viewports
 * @param {number} selectedPhotoIndex - the index of the selected photo
 */
export const Lightbox: React.FC<LightboxProps> = ({
  analyticsName,
  photoUrls,
  isOpen,
  handleClose,
  mobileTitle,
  footer,
  selectedPhotoIndex,
}) => {
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const photoRefs = useRef<Array<HTMLElement | RNView | null>>([]);
  const [primaryPhotoIndex, setPrimaryPhotoIndex] = useState<number>(
    selectedPhotoIndex ? selectedPhotoIndex : 0,
  );
  const containerRef = useRef<RNView>(null);
  const [panResponder, setPanResponder] = useState<PanResponderInstance | null>(null);
  const [isLandscape, setIsLandscape] = useState<boolean>(false);
  const offsetX = useRef(new Animated.Value(0)).current;
  const currentOffset = useRef(0);
  const carouselRef = useRef<RNView>(null);
  const lastNavigationSource = useRef<LastNavigationSource | null>(null);

  const primaryPhotoRefs = useRef<Array<RefObject<ReactNativeImage>>>(
    photoUrls.map((_) => createRef<ReactNativeImage>()),
  );

  const handleOnPress = (index: number) => {
    setPrimaryPhotoIndex(index);
  };

  // Check initial orientation on page load
  useEffect(() => {
    if (isClient) {
      if (Math.abs(window.orientation) === 90) {
        setIsLandscape(true);
      }
    }
  }, []);

  // Handle setting the primary photo index when the selected photo index changes
  useEffect(() => {
    if (selectedPhotoIndex !== null && selectedPhotoIndex !== undefined) {
      setPrimaryPhotoIndex(selectedPhotoIndex);
    }
  }, [selectedPhotoIndex]);

  // Handle scrolling to the primary photo when the primary photo index changes
  useEffect(() => {
    const photoItem = photoRefs.current[primaryPhotoIndex] as HTMLElement;
    photoItem?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    // Only focus if the last navigation was via keyboard arrow keys
    // This is done to prevent focus from being swapped when navigating
    // with the chevron buttons via spaces and tabs
    if (
      lastNavigationSource.current?.source === 'keyboard' &&
      !lastNavigationSource.current.keyboardActivated
    ) {
      photoItem?.focus();
    }
  }, [primaryPhotoIndex]);

  const handleRight = useCallback(
    (source: 'keyboard' | 'button', keyboardActivated = false) => {
      let newIndex = primaryPhotoIndex + 1;
      if (newIndex >= photoUrls.length) {
        newIndex = 0;
      }
      setPrimaryPhotoIndex(newIndex);
      return { source, keyboardActivated };
    },
    [primaryPhotoIndex, photoUrls.length],
  );

  const handleLeft = useCallback(
    (source: 'keyboard' | 'button', keyboardActivated = false) => {
      let newIndex = primaryPhotoIndex - 1;
      if (newIndex < 0) {
        newIndex = photoUrls.length - 1;
      }
      setPrimaryPhotoIndex(newIndex);
      return { source, keyboardActivated };
    },
    [primaryPhotoIndex, photoUrls.length],
  );

  // PanResponder for swiping images on mobile web and native
  useEffect(() => {
    const ANIMATION_DURATION = 300;
    const responder = PanResponder.create({
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: () => {
        offsetX.setValue(currentOffset.current);
      },
      onPanResponderMove: (evt, gestureState) => {
        const touches = evt.nativeEvent.touches;
        // Suppress horizontal scrolling when the user is zooming in on the image
        if (touches && touches.length < 2) {
          offsetX.setValue(currentOffset.current + gestureState.dx);
        }
      },
      onPanResponderRelease: (...args) => {
        const gestureState = args[1];
        const currentImageIndex = primaryPhotoIndex;
        // Swiping a third of the screen width will move to the next image
        const SWIPE_DISTANCE_THRESHOLD = 7;
        const threshold = windowWidth / SWIPE_DISTANCE_THRESHOLD;
        let newIndex = currentImageIndex;

        if (gestureState.dx > threshold) {
          newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : currentImageIndex;
        } else if (gestureState.dx < -threshold) {
          newIndex =
            currentImageIndex < photoUrls.length - 1 ? currentImageIndex + 1 : currentImageIndex;
        }

        if (newIndex !== currentImageIndex) {
          Animated.timing(offsetX, {
            toValue: -newIndex * windowWidth,
            duration: ANIMATION_DURATION,
            easing: Easing.linear,
            useNativeDriver: !isWeb,
          }).start(() => {
            currentOffset.current = -newIndex * windowWidth;
            setPrimaryPhotoIndex(newIndex);
            AccessibilityInfo.announceForAccessibility(`Image ${newIndex + 1}`);
          });
        } else {
          Animated.spring(offsetX, {
            toValue: currentOffset.current,
            useNativeDriver: !isWeb,
          }).start();
        }
      },
    });
    setPanResponder(responder);
  }, [windowWidth, offsetX, primaryPhotoIndex, photoUrls.length]);

  useEffect(() => {
    offsetX.setValue(-primaryPhotoIndex * windowWidth);
    currentOffset.current = -primaryPhotoIndex * windowWidth;
  }, [windowWidth, offsetX, primaryPhotoIndex]);

  // Facilitates keyboard navigation
  const handleKeyDownEvents: EventListener = useCallback(
    (e) => {
      if (e instanceof KeyboardEvent) {
        if (e.key === 'ArrowRight') {
          lastNavigationSource.current = handleRight('keyboard');
        } else if (e.key === 'ArrowLeft') {
          lastNavigationSource.current = handleLeft('keyboard');
        } else if (e.key === 'Escape') {
          handleClose();
        }
      }
    },
    [handleClose, handleLeft, handleRight],
  );

  const handleChevronPress = useCallback(
    (direction: 'left' | 'right') => (event: GestureResponderEvent | React.MouseEvent) => {
      let keyboardActivated = false;

      // Check if it's a keyboard event (for accessibility navigation)
      if ('nativeEvent' in event && event.nativeEvent instanceof KeyboardEvent) {
        keyboardActivated = true;
      }
      // Check if it's a mouse click event
      else if (event.type === 'click') {
        keyboardActivated = false;
      }

      lastNavigationSource.current =
        direction === 'left'
          ? handleLeft('button', keyboardActivated)
          : handleRight('button', keyboardActivated);
    },
    [handleLeft, handleRight],
  );

  useEffect(() => {
    // on desktop add event listeners for keyboard navigation
    if (isWeb) {
      document.addEventListener('keydown', handleKeyDownEvents);
    }
    return () => {
      if (isWeb) {
        document.removeEventListener('keydown', handleKeyDownEvents);
      }
    };
  }, [handleKeyDownEvents]);

  // Handle orientation changes
  useEffect(() => {
    if (windowWidth > windowHeight) {
      setIsLandscape(true);
    } else {
      setIsLandscape(false);
    }
  }, [windowHeight, windowWidth]);

  /**
   * Display the primary photo
   * This function will only render the images that are within the window size.
   * So 3 images before and 3 images after the current image.
   * @returns {React.JSX.Element[]} The rendered images.
   */
  const renderImages = () => {
    const WINDOW_SIZE = 2;
    const images: React.JSX.Element[] = [];
    for (let index = 0; index < photoUrls.length; index++) {
      if (index >= primaryPhotoIndex - WINDOW_SIZE && index <= primaryPhotoIndex + WINDOW_SIZE) {
        images.push(
          <Box
            key={`main-photos-${index}`}
            width={windowWidth}
            height="100%"
            aria-hidden={index !== primaryPhotoIndex}
            flexGrow={1}
          >
            <Image
              alt="Current primary photograph in lightbox"
              source={{
                uri: `${photoUrls[index]}${CABINET_QUERY_PARAMS_FOR_LIGHTBOX_PRIMARY}`,
              }}
              resizeMode="contain"
              height="100%"
              width="100%"
              mb="$10x"
              ref={primaryPhotoRefs.current[index]}
              enableZoom
            />
          </Box>,
        );
        continue;
      }
      images.push(
        <Box
          key={`main-photo-empty-${index}`}
          width={windowWidth}
          height="100%"
          aria-hidden={true}
        />,
      );
    }
    return images;
  };

  const viewportWidth = isWeb && isClient ? window.innerWidth : windowWidth;
  const isDesktop = viewportWidth >= BREAKPOINTS.MD;
  const isMobile = !isDesktop;

  return (
    <TamaguiDialog modal open={isOpen}>
      <TamaguiDialog.Portal>
        <LightboxDialogOverlay
          key="overlay"
          animation="accelerateDecelerateFastest"
          opacity={0.5}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
        />
        <LightboxDialogContent
          bordered
          elevate
          key="content"
          animateOnly={['transform', 'opacity']}
          animation={[
            'accelerateDecelerateFastest',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
          exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
          height="100%"
          width="100%"
          backgroundColor="$backgroundInversePureBlack"
          flexDirection="column"
          $smallerThanMD={{ justifyContent: 'center' }}
          px={0}
          focusStyle={{
            borderColor: '$backgroundInversePureBlack',
          }}
        >
          <TamaguiDialog.Title
            flexDirection="row"
            justifyContent="space-between"
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            onKeyDown={handleKeyDownEvents}
            $smallerThanMD={{ flexDirection: 'row-reverse' }}
            width="100%"
            data-testid="lightbox-content"
            mt={isLandscape && !isWeb ? '$20x' : null}
            $largerThanSM={{ px: '$20x' }}
            px="$10x"
            display="flex"
            alignItems="center"
            mb="$8x"
          >
            <Text
              tag="span"
              typography="$bodyMedium"
              color="$contentInversePrimary"
              data-testid="photo-count"
              width={64}
              $largerThanSM={{ textAlign: 'left' }}
              textAlign="right"
            >
              {primaryPhotoIndex + 1} of {photoUrls.length}
            </Text>
            <Box flex={1} justifyContent="center">
              {isMobile && mobileTitle}
            </Box>
            <TamaguiDialog.Close asChild>
              <Box
                $smallerThanMD={{
                  width: '$64x',
                }}
              >
                <CloseButton
                  analyticsName={`desktop-${analyticsName}-close`}
                  onPress={handleClose}
                  size="medium"
                  variant="inverse"
                  $smallerThanMD={{ display: 'none' }}
                  $largerThanSM={{ display: 'flex' }}
                />
                <ChevronBackButton
                  analyticsName={`mobile-${analyticsName}-close`}
                  onPress={handleClose}
                  size="medium"
                  variant="inverse"
                  $smallerThanMD={{ display: 'flex' }}
                  $largerThanSM={{ display: 'none' }}
                />
              </Box>
            </TamaguiDialog.Close>
          </TamaguiDialog.Title>
          <View
            width="100%"
            {...(panResponder && panResponder.panHandlers)}
            ref={carouselRef}
            aria-label={`Image Carousel, showing image ${primaryPhotoIndex + 1} of ${
              photoUrls.length
            }`}
            flex={1}
          >
            <Box
              ref={containerRef}
              height="100%"
              justifyContent="center"
              alignContent="center"
              aspectRatio="3/2"
              data-testid="lightbox-swipeable"
            >
              <Animated.View
                style={{
                  flexDirection: 'row',
                  width: windowWidth * photoUrls.length,
                  transform: [{ translateX: offsetX }],
                  marginBottom: 5,
                  flex: 1,
                }}
              >
                {renderImages()}
              </Animated.View>
              {isWeb && (
                <>
                  <ChevronBackButton
                    data-testid="lightbox-back-button"
                    analyticsName={`${analyticsName}-lightbox-back-button`}
                    size="medium"
                    $largerThanSM={{ frameSize: 'large', left: 32 }}
                    $smallerThanMD={{ display: 'none' }}
                    variant="inverse"
                    onPress={handleChevronPress('left')}
                    position="absolute"
                    left={0}
                    ml="$5x"
                    top="40%"
                    display={'flex'}
                  />
                  <ChevronForwardButton
                    data-testid="lightbox-forward-button"
                    analyticsName={`${analyticsName}-lightbox-forward-button`}
                    size="medium"
                    onPress={handleChevronPress('right')}
                    $largerThanSM={{ frameSize: 'large', right: 32 }}
                    $smallerThanMD={{ display: 'none' }}
                    variant="inverse"
                    position="absolute"
                    right={0}
                    mr="$5x"
                    top="40%"
                    display={'flex'}
                  />
                </>
              )}
              {isDesktop && isWeb && (
                <Box>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                    {photoUrls?.map((photo, index) => (
                      <CarouselPhoto
                        handleOnPress={handleOnPress}
                        photoUrl={photo}
                        index={index}
                        primaryPhotoIndex={primaryPhotoIndex}
                        isPrimary={index === primaryPhotoIndex}
                        photoRefs={photoRefs}
                        key={index}
                      />
                    ))}
                  </ScrollView>
                </Box>
              )}
              {isWeb && isDesktop && (
                <Box flexDirection="row" justifyContent="space-between" p="$8x" width="100%">
                  {footer}
                </Box>
              )}
            </Box>
          </View>
        </LightboxDialogContent>
      </TamaguiDialog.Portal>
    </TamaguiDialog>
  );
};

Lightbox.displayName = 'Lightbox';

/**
 * Carousel photo component props
 */
interface CarouselPhotoProps {
  /**
   * a function to handle pressing the photo
   */
  handleOnPress: (index: number) => void;
  /**
   * Url of the photo
   */
  photoUrl: string;
  /**
   * Index of the photo
   */
  index: number;
  /**
   * The primary photo position to conditionally load the photo
   */
  primaryPhotoIndex: number;
  /**
   * Whether the photo is the primary photo, for highlighting
   */
  isPrimary: boolean;
  /**
   * Refs for the photo items
   */
  photoRefs: RefObject<Array<HTMLElement | RNView | null>>;
}

/**
 * Carousel photo component
 */
const CarouselPhoto: React.FC<CarouselPhotoProps> = ({
  handleOnPress,
  photoUrl,
  index,
  isPrimary,
  photoRefs,
}) => {
  const onPress = useCallback(() => {
    handleOnPress(index);
  }, [handleOnPress, index]);

  const IMG_CONTAINER_HEIGHT = 65;
  const IMG_CONTAINER_WIDTH = 99;
  const CONTAINER_HEIGHT = 73;

  const photo = `${photoUrl}${CABINET_QUERY_PARAMS_LIGHTBOX_CAROUSEL}`;

  const handleRef = (el: HTMLElement | RNView | null) => {
    if (el && photoRefs.current) {
      photoRefs.current[index] = el;
    }
  };
  return (
    <PressableBox
      analyticsName={`lightbox-photo-item-${index}`}
      aria-label={`Lightbox carousel photo ${index + 1}`}
      borderColor={isPrimary ? '$borderTertiary' : '$backgroundInversePureBlack'}
      borderWidth="$2x"
      height={CONTAINER_HEIGHT}
      $smallerThanMD={{ display: 'none' }}
      onPress={onPress}
      data-testid={`photo-item-${index}`}
      px={0}
      ref={handleRef}
    >
      <Image
        alt={`carousel photograph ${index + 1}`}
        source={{ uri: photo }}
        resizeMode="cover"
        height={IMG_CONTAINER_HEIGHT}
        width={IMG_CONTAINER_WIDTH}
      />
    </PressableBox>
  );
};
